/* ==========================================================================
   BN Free Gift & Spend Threshold Rewards -- cart page adapter
   --------------------------------------------------------------------------
   Loaded only by sections/bn-gift-rewards.liquid, so only on /cart.

   On the cart page the rewards UI lives in its own Shopify section, and the
   theme's cart.js does not know it exists: CartItems.onCartUpdate only swaps
   #MainCart-{id} and #CartDrawer*-{id} for the one section it belongs to.
   Left alone, this section would keep showing a stale total after every
   quantity change -- the customer removes an item, drops back under $100, and
   the bar still says the gift is unlocked.

   Rather than fire a second request, this element joins the round trip the
   theme already makes. It pushes its own section id into the
   `cart:grouped-sections` collection so Shopify re-renders it alongside the
   main cart, then reads its refreshed markup back out of the same
   `cart:updated` payload.

   None of this is needed in the cart drawer: that section is re-rendered
   wholesale by the theme, so snippets/bn-gift-rewards.liquid stands alone
   there. This file is the cart-page-only difference.
   ========================================================================== */

(function () {
  'use strict';

  class BNGiftRewardsSection extends HTMLElement {
    connectedCallback() {
      if (this.__bnBound) return;
      this.__bnBound = true;

      this.sectionId = this.dataset.sectionId;
      this.onGroupedSections = this.collectSection.bind(this);
      this.onCartUpdated = this.render.bind(this);

      /* cart.js dispatches cart:grouped-sections on documentElement with
         bubbles: true, so a document-level listener catches it -- this is the
         same way the theme's own <main-cart> and <cart-drawer> register. */
      document.addEventListener('cart:grouped-sections', this.onGroupedSections);
      document.addEventListener('cart:updated', this.onCartUpdated);
    }

    disconnectedCallback() {
      document.removeEventListener('cart:grouped-sections', this.onGroupedSections);
      document.removeEventListener('cart:updated', this.onCartUpdated);
    }

    collectSection(event) {
      const list = event.detail && event.detail.sections;
      if (!list || !this.sectionId) return;
      if (list.indexOf(this.sectionId) === -1) list.push(this.sectionId);
    }

    render(event) {
      const sections = event.detail && event.detail.cart && event.detail.cart.sections;
      const markup = sections && sections[this.sectionId];

      /* No markup means this section wasn't part of the bundle -- some other
         script published the update. Leaving the stale DOM alone is safer than
         blanking it; the next real cart action will refresh it. */
      if (!markup) return;

      const fresh = new DOMParser()
        .parseFromString(markup, 'text/html')
        .getElementById('BNGiftRewards-' + this.sectionId);

      if (!fresh) return;

      /* Replacing innerHTML tears down the old <bn-gift-rewards> element and
         upgrades a new one, whose connectedCallback re-runs the enforcement
         pass against the freshly computed totals. className carries the
         empty-cart state. */
      this.innerHTML = fresh.innerHTML;
      this.className = fresh.className;
    }
  }

  if (!customElements.get('bn-gift-rewards-section')) {
    customElements.define('bn-gift-rewards-section', BNGiftRewardsSection);
  }
})();
